import Timeline, { DateHeader, SidebarHeader, TimelineHeaders } from "react-calendar-timeline";
import "react-calendar-timeline/lib/Timeline.css";
import OpsDateUtils from "../utils/OpsDateUtils";
import { makeStyles } from "@mui/styles";
import { ColorUtils } from "../utils/ColorUtils";
import { Box, Button, Typography } from "@mui/material";

const ONE_DAY = 1000 * 3600 * 24;
const ONE_YEAR = ONE_DAY * 365;

const BORDER_RADIUS = "0.5rem";
const BORDER = `1px solid ${ColorUtils.TABLE_BORDER}`;

const useStyles = makeStyles({
  timeline: {
    color: `${ColorUtils.FONT_LIGHT_GRAY} !important`,
    "& .rct-sidebar div:last-of-type": {
      width: "10.625rem !important",
    },

    "& .rct-sidebar-row": {
      textAlign: "center",
    },

    "& .rct-outer": {
      borderBottomLeftRadius: BORDER_RADIUS,
      borderBottomRightRadius: BORDER_RADIUS,
      borderLeft: BORDER,
      borderRight: BORDER,
    },

    "& .rct-item": {
      borderRadius: BORDER_RADIUS,
      background: `${ColorUtils.BLUE} !important`,
      color: `${ColorUtils.FONT_LIGHT_GRAY} !important`,
    },
    "& .group-item": {
      opacity: "0.5",
      zIndex: "30 !important",
      backgroundColor: `${ColorUtils.SUCCESS} !important`,
    },
  },
  headers: {
    background: "none !important",
    "& .rct-dateHeader": {
      backgroundColor: ColorUtils.TABLE_HEADER,
    },
    "& .rct-calendar-header": {
      borderBottom: "none",
      borderTopRightRadius: BORDER_RADIUS,
    },
  },
  sidebarHeader: {
    width: "10.625rem",
    backgroundColor: ColorUtils.TABLE_HEADER,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderTopLeftRadius: BORDER_RADIUS,
  },
  groupButton: {
    margin: "0 !important",
    padding: "0 !important",
    width: "100%",
    height: "100%",
    fontWeight: "initial !important",
    color: `${ColorUtils.FONT_LIGHT_GRAY} !important`,
  },
},
{ index: 1 },
);

export interface TimelineGroup {
  id: string;
  title: string;
  start_time: Date;
  end_time?: Date;
}
  
export interface TimelineItem {
  id: string;
  group: string;
  title: string;
  start_time: Date;
  end_time: Date;
  canMove?: boolean;
  canResize?: boolean;
  isGroup?: boolean;
}

interface Props {
  groups: TimelineGroup[];
  items: TimelineItem[];
  start: Date;
  end?: Date;
  updateStartTime?: (date: Date) => void; //To maintain the position once the timeline data is updated
  onGroupClick?: (instanceId: string) => void;
  onItemClick?: (instanceId: string) => void;
}

function TimelineComponent({ groups, items, start, end, updateStartTime, onGroupClick, onItemClick }: Props) {
  const classes = useStyles();

  const groupsToShow = (): boolean => {
    return groups.length !== 0;
  };

  //To prevent clicking on a group displayed as an item
  const handleOnItemClick = (itemId: string) => {
    if (!onItemClick) {
      return undefined;
    }
    const item = items.find((item) => item.id === itemId);
    const isGroupItem = item?.isGroup;
    if (!isGroupItem) {
      onItemClick(itemId);
    } else if (onGroupClick) {
      onGroupClick(itemId);
    }
  };

  //To prevent resetting the timeline to it's first possition
  const handleUpdateStartTime = (visibleTimeStart: number, visibleTimeEnd: number, updateScrollCanvas: any) => {
    if (!updateStartTime) {
      return undefined;
    }
    updateStartTime(new Date(visibleTimeStart));
    updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
  };

  return (
    <>
      {groupsToShow() ? (
        <Timeline
          defaultTimeStart={start}
          defaultTimeEnd={end ?? OpsDateUtils.addMonthsToDate(start, 1)}
          maxZoom={ONE_YEAR}
          minZoom={ONE_DAY}
          onTimeChange={handleUpdateStartTime}
          // timeSteps={TIME_STEPS}
          groups={groups}
          items={items}
          stackItems={true}
          sidebarWidth={170} //pxs
          lineHeight={36}
          className={classes.timeline}
          onItemClick={handleOnItemClick}
          groupRenderer={
            onGroupClick
              ? ({ group }: { group: TimelineGroup }) => (
                  <Button className={classes.groupButton} onClick={() => onGroupClick(group.id)}>
                    {group.title}
                  </Button>
                )
              : undefined
          }
        >
          <TimelineHeaders className={`${classes.headers} sticky`}>
            <SidebarHeader>
              {() => {
                return (
                  <Box className={classes.sidebarHeader}>
                    <Typography>Roles</Typography>
                  </Box>
                );
              }}
            </SidebarHeader>
            <DateHeader unit="month" labelFormat="MMM YYYY" />
            <DateHeader />
          </TimelineHeaders>
        </Timeline>
      ) : (
        <Typography variant="h6">There is no data to display</Typography>
      )}
    </>
  );
}

export default TimelineComponent;
